import { useMemo, useState } from 'react'
import { useT } from 'flyt/lib/language/language'
import { Button } from 'flyt/ui/Button/Button'
import { Checkbox } from 'flyt/ui/Checkbox/Checkbox'
import { SettingsIcon } from 'flyt/ui/Icons/Icons'
import { Popover } from 'flyt/ui/Popover/Popover'
import type { DataTableColumnsMenuProps } from './DataTable.types'
import { ColumnDragHandle } from './ColumnDragHandle'
import { useColumnReorder } from './useColumnReorder'
import { moveColumn, orderIds } from './tableOrdering'

/**
 * The columns menu — the unit's separately mountable visibility control: a
 * minimal cogwheel trigger opening a popover listing every column as a
 * checkbox row (label = the column header) toggling show/hide. A non-hideable
 * column is listed with a disabled toggle. The host owns the view and shares
 * it with the table (data down, events up); the menu itself holds no state
 * beyond the popover's open/closed.
 */
export function DataTableColumnsMenu({
  columns,
  view,
  onViewChange,
  groupingToggle,
}: DataTableColumnsMenuProps) {
  const t = useT('common')
  // Open state is held only so the OK button can close the panel.
  const [open, setOpen] = useState(false)
  const orderedColumns = useMemo(() => {
    const byId = new Map(columns.map((column) => [column.id, column]))
    return orderIds(
      view.order,
      columns.map((column) => column.id),
    ).flatMap((id) => byId.get(id) ?? [])
  }, [columns, view.order])

  const reorder = (source: string, target: string) => {
    const ids = orderedColumns.map((column) => column.id)
    const order = moveColumn(ids, source, target)
    if (order !== ids) onViewChange({ ...view, order })
  }
  const drag = useColumnReorder(reorder)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => { setOpen(next); if (!next) drag.clear() }}
      modal
      trigger={
        /* ⛔ A COGWHEEL, NOT A WORD. A settings glyph beside the upload action
           is chrome the reader finds when they go looking; spelling it out gave
           a minor view option the same weight on the row as the screen's real
           action. The word survives as the control's accessible name and as the
           panel's own heading. */
        <button
          type="button"
          aria-label={t('table.columns')}
          className="flex items-center rounded-md border border-border-control bg-surface p-2 text-text-muted transition-colors hover:bg-surface-muted hover:text-text"
        >
          <SettingsIcon width={18} height={18} />
        </button>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold tracking-wide text-text-muted uppercase">
          {t('table.columns')}
        </p>
        {/* EVERY COLUMN IS LISTED, hidden ones included: checking one back on
            is how a column comes back, and a reader who cannot see the hidden
            ones has no reason to believe they can return. */}
        <div className="flex flex-col gap-0.5">
          {orderedColumns.map((column, index) => {
            const hideable = column.hideable ?? true
            const visible = !view.hiddenIds.includes(column.id)
            return (
              <div key={column.id} data-column-id={column.id} {...drag.targetProps(column.id)}
                className={`group/column flex items-center gap-2 rounded-md px-1.5 py-1.5 text-sm text-text hover:bg-surface-muted ${drag.stateOf(column.id) === 'source' ? 'opacity-50' : drag.stateOf(column.id) === 'target' ? 'bg-accent-muted ring-2 ring-inset ring-accent' : ''}`}>
                <ColumnDragHandle label={column.header} active={drag.stateOf(column.id) === 'source'} axis="vertical"
                  {...drag.handleProps(column.id)} onMove={(offset) => {
                    const target = orderedColumns[index + offset]
                    if (target) reorder(column.id, target.id)
                  }} />
                <label className={`flex min-w-0 flex-1 items-center gap-2 ${hideable ? 'cursor-pointer' : ''}`}>
                {/* The wrapping label does not name a button-role checkbox — label it explicitly. */}
                <Checkbox
                  checked={visible}
                  disabled={!hideable}
                  aria-label={column.header}
                  onCheckedChange={(next) => {
                    onViewChange({
                      ...view,
                      hiddenIds: next
                        ? view.hiddenIds.filter((id) => id !== column.id)
                        : [...view.hiddenIds, column.id],
                    })
                  }}
                />
                <span>{column.header}</span>
                </label>
              </div>
            )
          })}
        </div>
        {/* The host-owned row-grouping flag, when offered: one toggle at the
            menu's bottom, set off from the column list by a divider. */}
        {groupingToggle !== undefined ? (
          <label className="flex cursor-pointer items-center gap-2 rounded-md border-t border-border px-1.5 pt-2.5 pb-1 text-sm text-text hover:bg-surface-muted">
            <Checkbox
              checked={groupingToggle.enabled}
              aria-label={groupingToggle.label}
              onCheckedChange={(next) => {
                groupingToggle.onChange(next)
              }}
            />
            <span>{groupingToggle.label}</span>
          </label>
        ) : null}
        {/* Explicit OK close, so dismissing never requires clicking outside. */}
        <div className="flex justify-end border-t border-border pt-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setOpen(false)
            }}
          >
            {t('actions.ok')}
          </Button>
        </div>
      </div>
    </Popover>
  )
}
